import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getDatabase, ref, get, remove, update } from 'firebase/database';
import { Save, Trash2, Edit2, Plus, Building2, MoreVertical, Copy, ChevronDown, ChevronRight } from 'lucide-react';
import { Dropdown } from 'react-bootstrap';
import { useBuildComponentTypes } from '../../contexts/BuildComponentTypesContext';
import { getMaterialTranslation, useMaterials } from '../../contexts/MaterialsContext';
import { buildComponentTypeDisplayName } from '../../Helpers/windowTypeName';
import { useBuildingsData } from '../../Helpers/BuildingSummariesContext';
import { translate } from '../../types/EnumTranslations';
import { MaintenanceIntervalType, BaseType, UtilityCategory, OutdoorCategory, HazardousState, RiskConstructionType, ColorEnum, HazardType } from '../../types/Enums';
import { ConfirmDeleteModal } from '../../Components/ConfirmDeleteModal';
import ComponentTypeUsageModal from '../../Components/ComponentTypeUsageModal';
import ComponentTypeUsageList, { ComponentUsageInfo } from '../../Components/ComponentTypeUsageList';
import { useUserNames } from '../../Helpers/useUserNames';
import { BaseComponentImpl } from '../../types/implementations';
import { resolveComponentLocation } from '../../Helpers/ComponentRoomUtils';
import { Building, BuildingSummary } from '../../types/protocols';
import TemplateEditModal from '../Förvaltning/TemplatesEditor/TemplateEditModal';
import LifetimeEditModal from '../Förvaltning/TemplatesEditor/LifetimeEditModal';
import { MaintenanceTemplate, EditForm, ComponentLifetime, CostUnit } from '../Förvaltning/TemplatesEditor/types';
import { maintenanceTemplateApi } from '../../api/maintenanceTemplateApi';
import { isTemplateArchived } from '../../Helpers/maintenanceTemplates';
import { recordActivity } from '../../api/activityApi';
import { CopyTemplatesFromTypeModal } from './CopyTemplatesFromTypeModal';
import { useToast } from '../../Components/ToastContext';

interface ComponentTypeDetailPaneProps {
    typeKey: string;
    onDeleted: () => void;
}

/**
 * Detail pane for editing a component type.
 * Shows name, baseType (read-only), lifetime settings, and maintenance templates.
 */
const ComponentTypeDetailPane: React.FC<ComponentTypeDetailPaneProps> = ({
    typeKey,
    onDeleted
}) => {
    const { types, getTranslation, getBaseType, getLifetime, getTemplates, refresh } = useBuildComponentTypes();
    const { buildings } = useBuildingsData();
    const { showToast } = useToast();

    const typeDefinition = types[typeKey];
    const { materials } = useMaterials();
    const baseType = getBaseType(typeKey);
    const lifetime = getLifetime(typeKey);
    const allTemplates = getTemplates(typeKey);
    // Archived templates (deleted while plans used them) are listed apart, with Återställ
    const templates = useMemo(() => allTemplates.filter(t => !isTemplateArchived(t)), [allTemplates]);
    const archivedTemplates = useMemo(() => allTemplates.filter(isTemplateArchived), [allTemplates]);
    const [showArchivedTemplates, setShowArchivedTemplates] = useState(false);
    const [restoringTemplateId, setRestoringTemplateId] = useState<string | null>(null);

    // Filter state for showing building-specific templates
    const [showBuildingSpecific, setShowBuildingSpecific] = useState<boolean>(false);

    // Convert building summaries to options for the dropdown
    const buildingOptions = useMemo(() => {
        if (!buildings) return [];
        return buildings.map(b => ({
            key: b.key || '',
            name: b.name || b.key || ''
        })).filter(b => b.key).sort((a, b) => a.name.localeCompare(b.name, 'sv'));
    }, [buildings]);

    // Helper to get building name from key
    const getBuildingName = useCallback((buildingKey: string): string => {
        if (!buildings) return buildingKey;
        const building = buildings.find(b => b.key === buildingKey);
        return building?.name || buildingKey;
    }, [buildings]);

    // Filter templates based on showBuildingSpecific toggle
    const filteredTemplates = useMemo(() => {
        if (showBuildingSpecific) {
            return templates; // Show all templates
        }
        // Hide building-specific templates
        return templates.filter(t => !t.buildingKey);
    }, [templates, showBuildingSpecific]);

    // Count building-specific templates
    const buildingSpecificCount = useMemo(() => {
        return templates.filter(t => !!t.buildingKey).length;
    }, [templates]);

    // Name editing state
    const [editedName, setEditedName] = useState('');
    const [isSavingName, setIsSavingName] = useState(false);
    const [nameError, setNameError] = useState<string | null>(null);
    const [nameSuccess, setNameSuccess] = useState(false);

    // Category editing state (for utility/outdoor types)
    const [editedCategory, setEditedCategory] = useState<string>('');

    // Special baseType props for windows/doors
    // Note: uvValue moved to instance after door/window type consolidation
    const [editedPaneCount, setEditedPaneCount] = useState<number | null>(null);
    const [editedDoorCategory, setEditedDoorCategory] = useState<string | null>(null);

    // Lifetime modal state
    const [showLifetimeModal, setShowLifetimeModal] = useState(false);
    const [isSavingLifetime, setIsSavingLifetime] = useState(false);
    const [lifetimeError, setLifetimeError] = useState<string | null>(null);
    const [lifetimeSuccess, setLifetimeSuccess] = useState(false);

    // Template modal state
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState<MaintenanceTemplate | null>(null);
    const [isNewTemplate, setIsNewTemplate] = useState(false);
    const [isSavingTemplate, setIsSavingTemplate] = useState(false);
    const [templateError, setTemplateError] = useState<string | null>(null);

    // Template delete confirmation state
    const [showTemplateDeleteConfirm, setShowTemplateDeleteConfirm] = useState(false);

    // Copy templates modal state
    const [showCopyModal, setShowCopyModal] = useState(false);
    const [templateToDelete, setTemplateToDelete] = useState<string | null>(null);
    // How many buildings' plans use the template asked to be deleted: any, and it is archived instead
    const [templateDeleteUsage, setTemplateDeleteUsage] = useState(0);
    const [isDeletingTemplate, setIsDeletingTemplate] = useState(false);

    // Delete state
    const [isCheckingUsage, setIsCheckingUsage] = useState(false);
    const [componentUsage, setComponentUsage] = useState<ComponentUsageInfo[] | null>(null);
    const [usageCheckError, setUsageCheckError] = useState<string | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    // "Instanser i byggnader" section: fetched on first expand, kept until the type changes
    const [instancesExpanded, setInstancesExpanded] = useState(false);
    const [isCheckingInstances, setIsCheckingInstances] = useState(false);
    const [instancesUsage, setInstancesUsage] = useState<ComponentUsageInfo[] | null>(null);
    const [instancesError, setInstancesError] = useState<string | null>(null);

    // "Detaljer" section: key and creator, name resolved on first expand
    const [detailsExpanded, setDetailsExpanded] = useState(false);
    const { userNames, loadUserNames } = useUserNames();

    // Initialize edited values when typeKey changes
    useEffect(() => {
        if (typeDefinition) {
            setEditedName(typeDefinition.translations?.sv || '');
            setEditedCategory(typeDefinition.category || '');
            // Initialize special props (uvValue moved to instance)
            setEditedPaneCount(typeDefinition.windowProps?.paneCount ?? null);
            setEditedDoorCategory(typeDefinition.doorProps?.doorCategory ?? null);
        }
        // Reset states
        setNameError(null);
        setNameSuccess(false);
        setLifetimeError(null);
        setLifetimeSuccess(false);
        setTemplateError(null);
        setComponentUsage(null);
        setUsageCheckError(null);
        setDeleteError(null);
        setInstancesExpanded(false);
        setInstancesUsage(null);
        setInstancesError(null);
        setDetailsExpanded(false);
    }, [typeKey, typeDefinition]);

    // Save name and category together
    const handleSaveNameAndCategory = useCallback(async () => {
        const trimmedName = editedName.trim();
        if (!trimmedName) {
            setNameError('Namn får inte vara tomt');
            return;
        }
        if (trimmedName.length > 100) {
            setNameError('Namnet får inte vara längre än 100 tecken');
            return;
        }

        setIsSavingName(true);
        setNameError(null);
        setNameSuccess(false);

        try {
            const db = getDatabase();
            // Build updates object
            const updates: Record<string, any> = {
                [`config/buildComponentTypes/${typeKey}/translations/sv`]: trimmedName
            };

            // Include category for utility/outdoor types
            if (baseType === BaseType.Utility || baseType === BaseType.Outdoor) {
                updates[`config/buildComponentTypes/${typeKey}/category`] = editedCategory || null;
            }

            // Include window props (uvValue moved to instance)
            if (baseType === BaseType.Window) {
                updates[`config/buildComponentTypes/${typeKey}/windowProps`] = {
                    paneCount: editedPaneCount || null
                };
            }

            // Include door props (modularSize moved to instance)
            if (baseType === BaseType.Door) {
                updates[`config/buildComponentTypes/${typeKey}/doorProps`] = {
                    doorCategory: editedDoorCategory || null
                };
            }

            // Atomic update
            await update(ref(db), updates);
            recordActivity('settings.updated', { targetKey: typeKey, label: trimmedName });
            await refresh();
            setNameSuccess(true);
            setTimeout(() => setNameSuccess(false), 2000);
        } catch (error) {
            console.error('Error saving:', error);
            setNameError('Kunde inte spara. Försök igen.');
        } finally {
            setIsSavingName(false);
        }
    }, [typeKey, editedName, editedCategory, baseType, editedPaneCount, editedDoorCategory, refresh]);

    // Save lifetime from modal
    const handleSaveLifetime = useCallback(async (lifetimeData: ComponentLifetime) => {
        setIsSavingLifetime(true);
        setLifetimeError(null);
        setLifetimeSuccess(false);

        try {
            await maintenanceTemplateApi.saveLifetime(typeKey, lifetimeData, { source: 'settings' });
            await refresh();
            setShowLifetimeModal(false);
            setLifetimeSuccess(true);
            setTimeout(() => setLifetimeSuccess(false), 2000);
        } catch (error) {
            console.error('Error saving lifetime:', error);
            setLifetimeError('Kunde inte spara livslängden. Försök igen.');
        } finally {
            setIsSavingLifetime(false);
        }
    }, [typeKey, refresh]);

    // Template modal handlers
    const handleEditTemplate = useCallback((template: MaintenanceTemplate) => {
        setEditingTemplate(template);
        setIsNewTemplate(false);
        setShowTemplateModal(true);
    }, []);

    const handleAddTemplate = useCallback(() => {
        setEditingTemplate(null);
        setIsNewTemplate(true);
        setShowTemplateModal(true);
    }, []);

    const handleSaveTemplate = useCallback(async (form: EditForm) => {
        setIsSavingTemplate(true);
        setTemplateError(null);

        try {
            // Ensure primaryComponentType is set
            const formWithType: EditForm = {
                ...form,
                primaryComponentType: form.primaryComponentType || typeKey
            };

            if (isNewTemplate) {
                await maintenanceTemplateApi.createTemplate(formWithType);
            } else if (editingTemplate) {
                await maintenanceTemplateApi.updateTemplate(editingTemplate.id, formWithType, { source: 'settings' });
            }

            await refresh();
            setShowTemplateModal(false);
        } catch (error) {
            console.error('Error saving template:', error);
            const message = error instanceof Error ? error.message : 'Kunde inte spara mallen. Försök igen.';
            setTemplateError(message);
        } finally {
            setIsSavingTemplate(false);
        }
    }, [typeKey, isNewTemplate, editingTemplate, refresh]);

    // Request template deletion (shows confirmation modal)
    const handleDeleteTemplateRequest = useCallback(async (templateId: string) => {
        setTemplateError(null);
        setIsDeletingTemplate(true);
        try {
            const usedBy = await maintenanceTemplateApi.getTemplateUsage(templateId);
            setTemplateDeleteUsage(usedBy.length);
            setTemplateToDelete(templateId);
            setShowTemplateDeleteConfirm(true);
        } catch (error) {
            console.error('Error reading template usage:', error);
            setTemplateError('Kunde inte kontrollera om mallen används. Försök igen.');
        } finally {
            setIsDeletingTemplate(false);
        }
    }, []);

    const handleRestoreTemplate = useCallback(async (templateId: string) => {
        setTemplateError(null);
        setRestoringTemplateId(templateId);
        try {
            await maintenanceTemplateApi.restoreTemplate(templateId);
            await refresh();
        } catch (error) {
            console.error('Error restoring template:', error);
            const message = error instanceof Error ? error.message : 'Kunde inte återställa mallen. Försök igen.';
            setTemplateError(message);
        } finally {
            setRestoringTemplateId(null);
        }
    }, [refresh]);

    // Confirm and execute template deletion
    const handleConfirmTemplateDelete = useCallback(async () => {
        if (!templateToDelete) return;

        setIsDeletingTemplate(true);
        setTemplateError(null);

        try {
            await maintenanceTemplateApi.deleteTemplate(templateToDelete);
            await refresh();
            setShowTemplateDeleteConfirm(false);
            setTemplateToDelete(null);
        } catch (error) {
            console.error('Error deleting template:', error);
            const message = error instanceof Error ? error.message : 'Kunde inte radera mallen. Försök igen.';
            setTemplateError(message);
            setShowTemplateDeleteConfirm(false);
        } finally {
            setIsDeletingTemplate(false);
        }
    }, [templateToDelete, refresh]);

    // Handle copy templates completion
    const handleCopyComplete = useCallback((count: number) => {
        refresh();
        const label = count === 1 ? 'mall kopierad' : 'mallar kopierade';
        setTemplateError(null);
        showToast(`${count} ${label}`, 'success');
    }, [refresh, showToast]);

    // Check usage across buildings
    // Note: This reads all buildings which can be slow for large databases.
    // Consider moving to a Cloud Function for better performance at scale.
    const checkUsage = useCallback(async (): Promise<ComponentUsageInfo[]> => {
        const db = getDatabase();
        const buildingsSnap = await get(ref(db, 'buildings'));
        const usage: ComponentUsageInfo[] = [];

        // Create a map of building keys to summaries from the context
        const buildingSummaryMap = new Map<string, BuildingSummary>();
        if (buildings) {
            buildings.forEach(b => {
                if (b.key) {
                    buildingSummaryMap.set(b.key, b);
                }
            });
        }

        if (buildingsSnap.exists()) {
            const allBuildings = buildingsSnap.val() as Record<string, unknown> | null;
            if (allBuildings) {
                Object.entries(allBuildings).forEach(([buildingKey, buildingData]: [string, unknown]) => {
                    if (!buildingData || typeof buildingData !== 'object') return;

                    // Skip buildings that have been removed (no summary exists)
                    const summary = buildingSummaryMap.get(buildingKey);
                    if (!summary) return;

                    const b = buildingData as Record<string, unknown>;
                    const buildingName = summary.name || buildingKey;

                    // Path is buildings/{key}/structure/components (not model/structure)
                    const structure = b.structure as Record<string, unknown> | undefined;
                    const components = structure?.components as Record<string, unknown> | undefined;
                    if (components) {
                        // Construct Building object for displayName() calls
                        const building: Building = {
                            key: buildingKey,
                            summary: summary,
                            model: {
                                key: buildingKey,
                                structure: structure as Building['model']['structure']
                            }
                        };

                        Object.entries(components).forEach(([componentKey, comp]: [string, unknown]) => {
                            if (!comp || typeof comp !== 'object') return;
                            const c = comp as Record<string, unknown>;

                            // Skip archived components
                            if (c.archivedAt) return;

                            const specificType = c.specificType as Record<string, unknown> | undefined;
                            if (specificType?.value === typeKey) {
                                // Wrap in BaseComponentImpl for displayName()
                                const componentImpl = new BaseComponentImpl({ ...c, key: componentKey });
                                const componentName = componentImpl.displayName(building);

                                // Resolve level/room location
                                const location = resolveComponentLocation(componentImpl, building.model.structure);

                                usage.push({
                                    buildingKey,
                                    buildingName,
                                    componentKey,
                                    componentName,
                                    levelDisplay: location.levelDisplay,
                                    roomDisplay: location.roomDisplay
                                });
                            }
                        });
                    }
                });
            }
        }

        return usage;
    }, [typeKey, buildings]);

    // Handle delete button click - show modal immediately, then check usage
    const handleDeleteClick = useCallback(async () => {
        // Show modal immediately with loading state
        setComponentUsage(null);
        setUsageCheckError(null);
        setShowDeleteModal(true);
        setIsCheckingUsage(true);

        try {
            const usage = await checkUsage();
            setComponentUsage(usage);
        } catch (error) {
            console.error('Error checking usage:', error);
            setUsageCheckError('Kunde inte kontrollera användning. Försök igen.');
            setShowDeleteModal(false);
        } finally {
            setIsCheckingUsage(false);
        }
    }, [checkUsage]);

    // Toggle the instances section; the buildings root is read on the first expand only
    const handleToggleInstances = useCallback(async () => {
        const expanding = !instancesExpanded;
        setInstancesExpanded(expanding);
        if (!expanding || instancesUsage !== null || isCheckingInstances) return;

        setInstancesError(null);
        setIsCheckingInstances(true);
        try {
            const usage = await checkUsage();
            setInstancesUsage(usage);
        } catch (error) {
            console.error('Error checking instances:', error);
            setInstancesError('Kunde inte hämta instanser. Försök igen.');
        } finally {
            setIsCheckingInstances(false);
        }
    }, [checkUsage, instancesExpanded, instancesUsage, isCheckingInstances]);

    // Toggle the details section; the creator's name is looked up on the first expand
    const handleToggleDetails = useCallback(() => {
        const expanding = !detailsExpanded;
        setDetailsExpanded(expanding);
        if (expanding && typeDefinition?.createdBy) {
            void loadUserNames([typeDefinition.createdBy]);
        }
    }, [detailsExpanded, typeDefinition?.createdBy, loadUserNames]);

    const formatCreatedAt = (dateString: string): string => {
        try {
            return new Date(dateString).toLocaleDateString('sv-SE', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    // Custom types are keyed {baseType}_custom_{pushKey} (web and iOS); anything else is predefined
    const isCustomType = typeKey.includes('_custom_');

    // undefined = not looked up yet, null = no such user
    const createdByCached = typeDefinition?.createdBy ? userNames[typeDefinition.createdBy] : undefined;
    const createdByName = createdByCached === undefined ? '…' : (createdByCached ?? 'Okänd användare');

    // Confirm deletion
    const handleConfirmDelete = useCallback(async () => {
        setIsDeleting(true);
        setDeleteError(null);

        try {
            const db = getDatabase();
            await remove(ref(db, `config/buildComponentTypes/${typeKey}`));
            recordActivity('settings.updated', { targetKey: typeKey });
            await refresh();
            setShowDeleteModal(false);
            onDeleted();
        } catch (error) {
            console.error('Error deleting component type:', error);
            setDeleteError('Kunde inte radera komponenttypen. Försök igen.');
            setShowDeleteModal(false);
        } finally {
            setIsDeleting(false);
        }
    }, [typeKey, refresh, onDeleted]);

    // Helper to format interval
    const formatInterval = (intervalValue: number, intervalType: MaintenanceIntervalType): string => {
        const typeLabels: Record<MaintenanceIntervalType, string> = {
            [MaintenanceIntervalType.days]: 'dagar',
            [MaintenanceIntervalType.weeks]: 'veckor',
            [MaintenanceIntervalType.months]: 'månader',
            [MaintenanceIntervalType.years]: 'år'
        };
        return `var ${intervalValue} ${typeLabels[intervalType] || 'år'}`;
    };

    if (!typeDefinition) {
        return (
            <div className="text-muted">
                Komponenttypen kunde inte hittas.
            </div>
        );
    }

    return (
        <div className="component-type-detail">
            {/* Header */}
            <div className="component-type-detail-header d-flex justify-content-between align-items-start">
                <div>
                    <h4 className="mb-2">
                        {(typeDefinition && buildComponentTypeDisplayName(typeDefinition, (materialKey) => materials[materialKey]?.translations?.sv))
                            || getTranslation(typeKey)}
                    </h4>
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                        <span className="badge bg-secondary">{baseType ? translate(baseType) : 'Okänd bastyp'}</span>
                        <button
                            type="button"
                            className="component-type-details-toggle"
                            onClick={handleToggleDetails}
                            aria-expanded={detailsExpanded}
                            aria-controls="component-type-details"
                        >
                            Detaljer
                            {detailsExpanded ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
                        </button>
                    </div>
                    {detailsExpanded && (
                        <dl id="component-type-details" className="component-type-details">
                            <dt>Nyckel</dt>
                            <dd><code className="component-type-key">{typeKey}</code></dd>
                            <dt>Skapad</dt>
                            <dd>
                                {typeDefinition.createdAt || typeDefinition.createdBy ? (
                                    <>
                                        {typeDefinition.createdAt ? formatCreatedAt(typeDefinition.createdAt) : 'Okänt datum'}
                                        {typeDefinition.createdBy && <> av {createdByName}</>}
                                    </>
                                ) : (
                                    <span className="text-muted">{isCustomType ? 'Okänt' : 'Fördefinierad typ'}</span>
                                )}
                            </dd>
                        </dl>
                    )}
                </div>
                <Dropdown align="end">
                    <Dropdown.Toggle
                        variant="link"
                        className="p-1 text-muted"
                        style={{ border: 'none', boxShadow: 'none' }}
                    >
                        <MoreVertical size={20} />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        <Dropdown.Item
                            className="text-danger"
                            onClick={handleDeleteClick}
                            disabled={isCheckingUsage || isDeleting}
                        >
                            <Trash2 size={14} className="me-2" />
                            {isCheckingUsage ? 'Kontrollerar...' : 'Radera komponenttyp'}
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </div>

            {/* Name & Category Section */}
            <div className="component-type-detail-section">
                <div className="mb-3">
                    <label className="form-label fw-medium">Namn</label>
                    <input
                        type="text"
                        className={`form-control ${nameError ? 'is-invalid' : ''}`}
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                        placeholder="Namn på komponenttypen"
                    />
                    {nameError && <div className="invalid-feedback">{nameError}</div>}
                </div>

                {/* Category field - Only for utility/outdoor types */}
                {(baseType === BaseType.Utility || baseType === BaseType.Outdoor) && (
                    <div className="mb-3">
                        <label className="form-label fw-medium">Kategori</label>
                        <select
                            className="form-select"
                            value={editedCategory}
                            onChange={(e) => setEditedCategory(e.target.value)}
                        >
                            <option value="">Välj kategori...</option>
                            {baseType === BaseType.Utility &&
                                Object.values(UtilityCategory)
                                    .sort((a, b) => translate(a).localeCompare(translate(b), 'sv'))
                                    .map(category => (
                                        <option key={category} value={category}>{translate(category)}</option>
                                    ))
                            }
                            {baseType === BaseType.Outdoor &&
                                Object.values(OutdoorCategory)
                                    .sort((a, b) => translate(a).localeCompare(translate(b), 'sv'))
                                    .map(category => (
                                        <option key={category} value={category}>{translate(category)}</option>
                                    ))
                            }
                        </select>
                    </div>
                )}

                {/* Window Properties - only paneCount (uvValue moved to instance) */}
                {baseType === BaseType.Window && (
                    <div className="mb-3">
                        <label className="form-label fw-medium">Antal glas</label>
                        <select
                            className="form-select"
                            value={editedPaneCount ?? ''}
                            onChange={(e) => setEditedPaneCount(e.target.value ? parseInt(e.target.value) : null)}
                        >
                            <option value="">Ej specificerat</option>
                            <option value="1">1-glas</option>
                            <option value="2">2-glas</option>
                            <option value="3">3-glas</option>
                        </select>
                    </div>
                )}

                {/* Door Properties */}
                {baseType === BaseType.Door && (
                    <div className="mb-3">
                        <label className="form-label fw-medium">Dörrkategori</label>
                        <select
                            className="form-select"
                            value={editedDoorCategory ?? ''}
                            onChange={(e) => setEditedDoorCategory(e.target.value || null)}
                        >
                            <option value="">Ingen kategori (visas under "Okategoriserade")</option>
                            <option value="interior_door">Innerdörr</option>
                            <option value="exterior_door">Ytterdörr</option>
                        </select>
                    </div>
                )}

                <button
                    className={`btn ${nameSuccess ? 'btn-success' : 'btn-primary'}`}
                    onClick={handleSaveNameAndCategory}
                    disabled={isSavingName || (
                        editedName === (typeDefinition.translations?.sv || '') &&
                        editedCategory === (typeDefinition.category || '') &&
                        editedPaneCount === (typeDefinition?.windowProps?.paneCount ?? null) &&
                        editedDoorCategory === (typeDefinition?.doorProps?.doorCategory ?? null)
                    )}
                >
                    {isSavingName ? (
                        <span className="spinner-border spinner-border-sm" />
                    ) : nameSuccess ? (
                        'Sparat!'
                    ) : (
                        <>
                            <Save size={16} className="me-1" />
                            Spara
                        </>
                    )}
                </button>
            </div>

            {/* Lifetime Section */}
            <div className="component-type-detail-section">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">Livslängd & Ersättningskostnad</h6>
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => setShowLifetimeModal(true)}
                    >
                        <Edit2 size={14} className="me-1" />
                        Redigera
                    </button>
                </div>
                {lifetimeError && (
                    <div className="alert alert-danger py-2 mb-2">{lifetimeError}</div>
                )}
                {lifetimeSuccess && (
                    <div className="alert alert-success py-2 mb-2">Sparat!</div>
                )}
                {lifetime ? (
                    <div className="lifetime-display">
                        <div className="row g-3">
                            <div className="col-md-4">
                                <span className="text-muted small">Förväntad livslängd</span>
                                <div className="fw-medium">{lifetime.expectedLifetimeYears} år</div>
                            </div>
                            <div className="col-md-4">
                                <span className="text-muted small">Ersättningskostnad</span>
                                <div className="fw-medium">
                                    {lifetime.replacementCost || 0} {lifetime.costUnit === 'per_sqm' ? 'kr/kvm' : lifetime.costUnit === 'per_lm' ? 'kr/lm' : 'kr/st'}
                                </div>
                            </div>
                            {lifetime.directionalLifetimes && lifetime.directionalLifetimes.length > 0 && (
                                <div className="col-12 mt-2">
                                    <span className="text-muted small">Väderstrecksberoende livslängder</span>
                                    <div className="d-flex flex-wrap gap-2 mt-1">
                                        {lifetime.directionalLifetimes.map((dl, idx) => (
                                            <span key={idx} className="badge bg-secondary">
                                                {translate(dl.direction)}: {dl.expectedLifetimeYears} år
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <span className="text-muted fst-italic">Ingen livslängd konfigurerad</span>
                )}
            </div>

            {/* Templates Section */}
            <div className="component-type-detail-section">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">Underhållsmallar</h6>
                    <div className="d-flex gap-2">
                        <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={handleAddTemplate}
                        >
                            <Plus size={14} className="me-1" />
                            Lägg till mall
                        </button>
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setShowCopyModal(true)}
                        >
                            <Copy size={14} className="me-1" />
                            Kopiera från annan typ
                        </button>
                    </div>
                </div>

                {/* Filter checkbox for building-specific templates */}
                {buildingSpecificCount > 0 && (
                    <div className="form-check mb-3">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id="show-building-specific"
                            checked={showBuildingSpecific}
                            onChange={(e) => setShowBuildingSpecific(e.target.checked)}
                        />
                        <label className="form-check-label small" htmlFor="show-building-specific">
                            Visa fastighetsspecifika mallar ({buildingSpecificCount} st)
                        </label>
                    </div>
                )}

                {templateError && (
                    <div className="alert alert-danger py-2 mb-3">{templateError}</div>
                )}

                {filteredTemplates.length > 0 ? (
                    <div className="templates-list">
                        {filteredTemplates.map(template => (
                            <div key={template.id} className="template-item">
                                <div className="template-info">
                                    <div className="template-name d-flex align-items-center gap-2">
                                        {template.name}
                                        {template.buildingKey && (
                                            <span
                                                className="badge bg-secondary d-inline-flex align-items-center gap-1"
                                                style={{ fontSize: '0.7rem', fontWeight: 'normal' }}
                                                title={`Begränsad till: ${getBuildingName(template.buildingKey)}`}
                                            >
                                                <Building2 size={10} />
                                                {getBuildingName(template.buildingKey)}
                                            </span>
                                        )}
                                    </div>
                                    <div className="template-meta">
                                        {formatInterval(template.interval_value, template.interval_type)}
                                        {template.cost > 0 && (
                                            <> &bull; {template.cost} {template.cost_unit === 'per_sqm' ? 'kr/kvm' : template.cost_unit === 'per_lm' ? 'kr/lm' : 'kr/st'}</>
                                        )}
                                    </div>
                                </div>
                                <div className="d-flex gap-2">
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => handleEditTemplate(template)}
                                    >
                                        <Edit2 size={14} />
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => handleDeleteTemplateRequest(template.id)}
                                        disabled={isDeletingTemplate}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-muted fst-italic">
                        {templates.length > 0 && !showBuildingSpecific
                            ? 'Inga globala underhållsmallar. Aktivera filtret ovan för att se fastighetsspecifika mallar.'
                            : 'Inga underhållsmallar konfigurerade för denna typ.'}
                    </div>
                )}

                {archivedTemplates.length > 0 && (
                    <div className="mt-3">
                        <button
                            type="button"
                            className="component-type-section-toggle"
                            onClick={() => setShowArchivedTemplates(open => !open)}
                            aria-expanded={showArchivedTemplates}
                            aria-controls="component-type-archived-templates"
                        >
                            <span>Arkiverade mallar ({archivedTemplates.length})</span>
                            {showArchivedTemplates ? <ChevronDown size={18} aria-hidden="true" /> : <ChevronRight size={18} aria-hidden="true" />}
                        </button>
                        {showArchivedTemplates && (
                            <div id="component-type-archived-templates" className="component-type-section-body">
                                <p className="small text-muted">
                                    Borttagna medan de användes i underhållsplaner. Aktiviteterna finns kvar i planerna, men mallarna erbjuds inte för nya aktiviteter.
                                </p>
                                <div className="templates-list">
                                    {archivedTemplates.map(template => (
                                        <div key={template.id} className="template-item">
                                            <div className="template-info">
                                                <div className="template-name text-muted">{template.name}</div>
                                                <div className="template-meta">
                                                    {formatInterval(template.interval_value, template.interval_type)}
                                                </div>
                                            </div>
                                            <button
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() => handleRestoreTemplate(template.id)}
                                                disabled={restoringTemplateId !== null}
                                            >
                                                Återställ
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Instances Section (collapsible, fetched on first expand) */}
            <div className="component-type-detail-section">
                <button
                    type="button"
                    className="component-type-section-toggle"
                    onClick={handleToggleInstances}
                    aria-expanded={instancesExpanded}
                    aria-controls="component-type-instances"
                >
                    <span>Instanser i byggnader</span>
                    {instancesExpanded ? <ChevronDown size={18} aria-hidden="true" /> : <ChevronRight size={18} aria-hidden="true" />}
                </button>
                {instancesExpanded && (
                    <div id="component-type-instances" className="component-type-section-body">
                        {isCheckingInstances ? (
                            <div className="d-flex align-items-center text-muted">
                                <span className="spinner-border spinner-border-sm me-2" />
                                Söker i byggnader...
                            </div>
                        ) : instancesError ? (
                            <div className="alert alert-danger py-2 mb-0">{instancesError}</div>
                        ) : instancesUsage ? (
                            <ComponentTypeUsageList usage={instancesUsage} />
                        ) : null}
                    </div>
                )}
            </div>

            {/* Additional Metadata Section - only show if any values are set */}
            {(() => {
                const hasMaterial = typeDefinition.material?.value && Array.isArray(typeDefinition.material.value) && typeDefinition.material.value.length > 0;
                const hasHazardous = typeDefinition.hazardous?.value && typeof typeDefinition.hazardous.value === 'object' &&
                    Object.values(typeDefinition.hazardous.value).some(v => v);
                const hasRiskConstructions = typeDefinition.buildingRisks?.value?.risks &&
                    Array.isArray(typeDefinition.buildingRisks.value.risks) &&
                    typeDefinition.buildingRisks.value.risks.length > 0;
                const hasComment = typeDefinition.comment?.value && typeof typeDefinition.comment.value === 'string' && typeDefinition.comment.value.trim() !== '';
                const hasColor = typeDefinition.color?.value?.colorEnum;

                const hasAnyMetadata = hasMaterial || hasHazardous || hasRiskConstructions || hasComment || hasColor;

                if (!hasAnyMetadata) return null;

                // Helper to translate hazard state
                const translateHazardState = (state: HazardousState | undefined): string => {
                    if (!state) return '';
                    return translate(state);
                };

                // Map property names to HazardType enum for translation
                const hazardPropertyToEnum: Record<string, HazardType> = {
                    asbestos: HazardType.HazardAsbestos,
                    pcb: HazardType.HazardPcb,
                    led: HazardType.HazardLed,
                    arsenic: HazardType.HazardArsenic,
                    blueConcrete: HazardType.HazardBlueConcrete,
                    radon: HazardType.HazardRadon
                };

                return (
                    <div className="component-type-detail-section">
                        <h6 className="mb-3">Övriga inställningar</h6>
                        <div className="metadata-display">
                            {hasColor && (
                                <div className="metadata-item mb-2">
                                    <span className="text-muted small d-block">Färg</span>
                                    <span className="fw-medium">{translate(typeDefinition.color!.value.colorEnum as ColorEnum)}</span>
                                </div>
                            )}
                            {hasMaterial && (
                                <div className="metadata-item mb-2">
                                    <span className="text-muted small d-block">Material</span>
                                    <span className="fw-medium">{(typeDefinition.material!.value as string[]).map(m => getMaterialTranslation(m)).join(', ')}</span>
                                </div>
                            )}
                            {hasHazardous && (
                                <div className="metadata-item mb-2">
                                    <span className="text-muted small d-block">Farliga ämnen</span>
                                    <div className="d-flex flex-wrap gap-2 mt-1">
                                        {Object.entries(typeDefinition.hazardous!.value as Record<string, HazardousState>)
                                            .filter(([, state]) => state)
                                            .map(([hazard, state]) => (
                                                <span key={hazard} className="badge bg-warning text-dark">
                                                    {hazardPropertyToEnum[hazard] ? translate(hazardPropertyToEnum[hazard]) : hazard}: {translateHazardState(state)}
                                                </span>
                                            ))
                                        }
                                    </div>
                                </div>
                            )}
                            {hasRiskConstructions && (
                                <div className="metadata-item mb-2">
                                    <span className="text-muted small d-block">Riskkonstruktioner</span>
                                    <div className="d-flex flex-wrap gap-2 mt-1">
                                        {(typeDefinition.buildingRisks!.value.risks as RiskConstructionType[]).map((risk, idx) => (
                                            <span key={idx} className="badge bg-secondary">
                                                {translate(risk)}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {hasComment && (
                                <div className="metadata-item mb-2">
                                    <span className="text-muted small d-block">Kommentar</span>
                                    <span className="fw-medium">{typeDefinition.comment!.value}</span>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })()}

            {/* Error messages for delete operations */}
            {(usageCheckError || deleteError) && (
                <div className="alert alert-danger py-2 mb-3">
                    {usageCheckError || deleteError}
                </div>
            )}

            {/* Lifetime Edit Modal */}
            <LifetimeEditModal
                show={showLifetimeModal}
                lifetime={lifetime}
                componentType={typeKey}
                onSave={handleSaveLifetime}
                onCancel={() => setShowLifetimeModal(false)}
                saving={isSavingLifetime}
            />

            {/* Template Edit Modal */}
            <TemplateEditModal
                show={showTemplateModal}
                template={editingTemplate}
                componentType={typeKey}
                componentTypeKey={typeKey}
                componentTypeName={getTranslation(typeKey)}
                isNewTemplate={isNewTemplate}
                onSave={handleSaveTemplate}
                onCancel={() => setShowTemplateModal(false)}
                saving={isSavingTemplate}
                buildings={buildingOptions}
                baseType={baseType}
            />

            {/* Template Delete Confirmation Modal */}
            <ConfirmDeleteModal
                show={showTemplateDeleteConfirm}
                onHide={() => {
                    setShowTemplateDeleteConfirm(false);
                    setTemplateToDelete(null);
                }}
                onConfirm={handleConfirmTemplateDelete}
                title={templateDeleteUsage > 0 ? 'Arkivera underhållsmall' : 'Radera underhållsmall'}
                message={templateDeleteUsage > 0
                    ? `Mallen används i underhållsplanen för ${templateDeleteUsage === 1 ? '1 byggnad' : `${templateDeleteUsage} byggnader`} och arkiveras i stället för att raderas. Aktiviteterna finns kvar i planerna, men mallen erbjuds inte längre för nya aktiviteter. Du kan återställa den under Arkiverade mallar.`
                    : 'Är du säker på att du vill radera denna underhållsmall? Denna åtgärd kan inte ångras.'}
                confirmButtonText={templateDeleteUsage > 0 ? 'Arkivera' : 'Radera'}
            />

            {/* Delete Confirmation/Blocking Modal */}
            <ComponentTypeUsageModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                componentTypeName={getTranslation(typeKey)}
                canDelete={componentUsage !== null && componentUsage.length === 0}
                usage={componentUsage || undefined}
                onConfirmDelete={handleConfirmDelete}
                isDeleting={isDeleting}
                isCheckingUsage={isCheckingUsage}
            />

            {/* Copy Templates Modal */}
            <CopyTemplatesFromTypeModal
                show={showCopyModal}
                onHide={() => setShowCopyModal(false)}
                targetTypeKey={typeKey}
                onCopyComplete={handleCopyComplete}
            />
        </div>
    );
};

export default ComponentTypeDetailPane;
